import { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik, FormikProps } from 'formik';

import { FormField } from 'components/forms/FormField';
import { GenericLoading } from 'components/ui/Loader';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { usePanel } from 'hooks/usePanel';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';

import {
  PlateformeOption,
  addPersonneMoralePlateforme,
  getAllPlateformes,
} from '../api/personneMoralePlateformesApi';
import {
  AddPersonneMoralePlateformeFieldNames,
  AddPersonneMoralePlateformeFormValues,
  addPersonneMoralePlateformeFormConfig,
} from './addPersonneMoralePlateformeFormConfig';

interface AddPersonneMoralePlateformeFormProps {
  personneMoraleId: string;
  existingPlateformeIds: string[];
  onSuccess: () => void;
}

export const AddPersonneMoralePlateformeForm: React.FC<AddPersonneMoralePlateformeFormProps> = ({
  personneMoraleId,
  existingPlateformeIds,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<AddPersonneMoralePlateformeFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [availableOrgs, setAvailableOrgs] = useState<PlateformeOption[]>([]);
  const { setPanelActions, closePanel } = usePanel();

  // Charger les plateformes disponibles (non encore rattachées)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await getAllPlateformes();
        if (cancelled) return;
        const attachedSet = new Set(existingPlateformeIds);
        setAvailableOrgs(all.filter(o => !attachedSet.has(o.id)));
      } catch {
        toasterService.showToast({
          message: 'Impossible de charger la liste des plateformes.',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [existingPlateformeIds]);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: !isFormValid || isLoading,
      onSubmit: () => {
        formikRef.current?.submitForm();
      },
    });
  }, [setPanelActions, isFormValid, isLoading]);

  const onSubmit = async (values: AddPersonneMoralePlateformeFormValues) => {
    setIsLoading(true);
    try {
      const res = await addPersonneMoralePlateforme(
        personneMoraleId,
        values.plateformeId,
      );
      if (!res.isSuccess) {
        toasterService.showToast({
          message: res.errorMessage ?? 'Échec du rattachement',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
        return;
      }
      toasterService.showToast({
        message: 'Plateforme rattachée.',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Green,
      });
      onSuccess();
      closePanel();
    } catch (e: any) {
      toasterService.showToast({
        message: e?.response?.data?.error?.message ?? 'Erreur inattendue',
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}

      <Formik
        enableReinitialize
        initialValues={addPersonneMoralePlateformeFormConfig.initialValues}
        validationSchema={addPersonneMoralePlateformeFormConfig.validationSchema}
        onSubmit={onSubmit}
        innerRef={formikRef}
      >
        {({ isValid, errors, touched }) => {
          if (isValid !== isFormValid) {
            setIsFormValid(isValid);
          }
          return (
            <Form className="flex flex-col gap-4 p-4">
              <FormField
                name={AddPersonneMoralePlateformeFieldNames.PlateformeId}
                label="Plateforme"
                required
                error={
                  touched.plateformeId
                    ? (errors.plateformeId as string | undefined)
                    : undefined
                }
              >
                <Field
                  as="select"
                  name={AddPersonneMoralePlateformeFieldNames.PlateformeId}
                  className="w-full rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">— Choisir une plateforme —</option>
                  {availableOrgs.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.displayName}
                    </option>
                  ))}
                </Field>
              </FormField>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
